import axios from 'axios';
import * as Sentry from '@sentry/react';
import * as selectConfig from 'store/slices/config/selectConfig';
import * as selectTokens from 'store/slices/config/selectTokens';
import * as selectSession from 'store/slices/session/selectSession';
import * as selectFeatureFlags from 'store/slices/config/selectFeatureFlags';

import HostMessageController from 'embeddings/HostMessageController';
import { delayByAttempt } from 'utils/fetchRetryDelay';
import { isEmbedded, mapCamel, getEnv } from 'utils/utils';

export function getStatusCodeRule(code) {
  const statusCodeRule = {
    // default options
    shouldRetry: true,
    disableSentry: false,
    shouldRequestTokenFromParent: false,
  };

  switch (code) {
    case 500:
    case 400:
    case 422:
    case 403:
    case 404:
      statusCodeRule.shouldRetry = false;
      break;
    case 502:
    case 503:
      // retain default options
      break;
    case 409:
    case 410:
      statusCodeRule.shouldRetry = false;
      statusCodeRule.disableSentry = true;
      break;
    case 401:
      statusCodeRule.shouldRetry = false;
      statusCodeRule.shouldRequestTokenFromParent = isEmbedded();
      statusCodeRule.disableSentry = true;
      break;
    default:
      break;
  }

  return statusCodeRule;
}

export const MAX_ATTEMPTS = 3;

export const getAxiosRequestConfig = (axiosConfig, state) => {
  axiosConfig.headers.Authorization = selectTokens.api(state);

  if (axiosConfig.headers['X-Integration-Token'] === undefined) {
    // integration token was not provided, so we should add it to the request
    const integrationToken = selectTokens.integrationToken(state);
    if (integrationToken) axiosConfig.headers['X-Integration-Token'] = integrationToken;
  }

  return axiosConfig;
};

export const handleAxiosResponse = (response) => {
  if (response.data) {
    response.data = mapCamel(response.data);
  }
  return response;
};

export async function handleTokenRefresh(state) {
  const embeddedTargetOrigin = selectSession.embeddedTargetOrigin(state);
  const messenger = new HostMessageController(window.parent, embeddedTargetOrigin, {
    debug: getEnv() !== 'production',
  });
  const newToken = await messenger.requestNewToken();
  return newToken;
}

export const handleAxiosError = async (error, instance, state) => {
  const { config, response, request } = error;

  const statusCodeRule = getStatusCodeRule(response?.status);
  const attemptsMade = config.attempts + 1;

  // Retry
  if (statusCodeRule.shouldRetry && attemptsMade < MAX_ATTEMPTS) {
    await delayByAttempt(attemptsMade);
    Sentry.addBreadcrumb({
      category: 'console',
      message: `Axios automatic retry: ${attemptsMade + 1}`,
      level: 'info',
    });
    return instance.request({ ...config, attempts: attemptsMade });
  }

  // Redirect
  if (statusCodeRule.shouldRequestTokenFromParent) {
    if (!selectFeatureFlags.enableEmbeddedMessenger(state)) return Promise.reject(error); // if the embedded messenger is not enabled, all we can do is reject with the 401 error

    try {
      if (attemptsMade <= 1) {
        // only retry the request once. otherwise we risk sitting in an endless loop of retries until the host sends a good token
        const newToken = await handleTokenRefresh(state);
        // repeat the same request but with the updated token
        config.headers['X-Integration-Token'] = newToken;
        return instance.request({ ...config, attempts: attemptsMade });
      }
    } catch (tokenRefreshError) {
      return Promise.reject(tokenRefreshError);
    }
  }

  // Report
  if (!statusCodeRule.disableSentry) {
    Sentry.withScope((scope) => {
      const url = new URL(request?.responseURL || `${error?.config?.baseURL}${error?.config?.url}`);

      const code = String(error?.response?.status || error?.response?.code || 'No response');
      const method = error?.config?.method;
      const path = url?.pathname?.split('/')[3] || url?.pathname; // avoid including a path param in the finger print such as /share/:id

      // group errors together based on their request and response
      const fingerprint = [method, path, code];

      if (response?.data?.code) {
        // prefer the "code" property if available for grouping
        fingerprint.push(`code:${response.data.code}`);
      } else if (response?.data?.detail) {
        fingerprint.push(`detail:${response.data.detail}`);
      }
      scope.setFingerprint(fingerprint);
      // add the response data to the event for easier debugging
      scope.setExtra('response', error.response?.data || 'No response data');
      scope.setExtra('url', url.toString());
      scope.setExtra('method', method);
      scope.setExtra('status', code);
      // provide a better faceup error message in sentry
      error.sentryMessage = `${code} on ${method?.toUpperCase()} - /${path}`;
      Sentry.captureException(error);
    });
  }

  return Promise.reject(error);
};

export function getAxiosInstance(state) {
  const defaultOptions = {
    baseURL: selectConfig.apiUrl(state),
    method: 'get',
    headers: {
      'Content-Type': 'application/json',
    },
    attempts: 0,
  };

  // Create instance
  const instance = axios.create(defaultOptions);

  // Set the AUTH token for any request
  instance.interceptors.request.use((config) => getAxiosRequestConfig(config, state));

  instance.interceptors.response.use(
    /* on fulfilled */
    (response) => handleAxiosResponse(response),

    /* on rejected */
    async (error) => handleAxiosError(error, instance, state)
  );

  return instance;
}

export default { getAxiosInstance };
